import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { toClientDateTime } from '../../../shared/Helpers/timezones';
import { SessionType as SessionTypeEnum } from '../../../shared/Helpers/constants';
import { CustomAlert, CustomButton, CustomSelect, Picker, SearchSelect } from '../../../shared/uiComponents';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { Client, SessionType } from '../../../shared/Assets/sessionDetailsSVGs';
import { AdminTypes } from '../../../components/Menu';
import AddContentAdmin from './addSessionAdmin';
import { TimeInputsWrapper } from '../../Reports/helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { createEvent } from '../../../redux/State/clientSlice/calendarSlice';
import { CreateEventProperties, SessionTypeProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { getUserClients } from '../../../redux/State/clientSlice/userClientSlice';
import { setAddScheduledSession } from '../../../redux/State/clientSlice/calendarHelperSlice';
import { getSessionTypes } from '../../../redux/State/clientSlice/sessionSlice';
import {
	clearDiscretionarySessionState,
	getUserRemainingHours,
} from '../../../redux/State/userSlice/userSessionAllocationSlice';

const todayDayjs = dayjs();

const AddContent = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [pageSize, setPageSize] = useState<number>(10);
	const [sessionTypeId, setSessionTypeId] = useState<number>(0);
	const [clientList, setClientList] = useState<SelectDataProperties[]>([]);
	const [dateValue, setDateValue] = useState<Dayjs | null>(todayDayjs);
	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(null);
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [sessionTypes, setSessionTypes] = useState<SessionTypeProperties[]>([]);
	const userSessionAllocation = useSelector((state) => state.userSessionAllocation.userRemainingHours);

	const sessionTypesData = useSelector((state) => state.session.sessionTypes);
	const userId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const userClients = useSelector((state) => state.userClient.userClients);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);

	// Client selection is local to this modal so it never mutates the global calendar filter.
	const [selectedClient, setSelectedClient] = useState<SelectDataProperties | null>(null);
	const [searchString, setSearchString] = useState<string>('');
	const clientId = selectedClient?.id;

	const isBT = role?.section.id === AdminTypes.bt;

	const { hasError, message } = useCompareDates(startTimeValue, endTimeValue);

	useEffect(() => {
		const { day, month, year } = selectedDay;
		const selectedDateValue = new Date(year, month, day);
		if (!clientId) return;
		dispatch(
			getSessionTypes({
				clientId,
				date: dateNormalizer(dayjs(selectedDateValue)),
				userId,
			}),
		);
	}, [dispatch, clientId, selectedDay, userId]);

	useEffect(() => {
		if (!userId) return;
		const timeout = setTimeout(() => {
			dispatch(
				getUserClients({
					userId,
					page: '1',
					pageSize: pageSize.toString(),
					searchString,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					if (!response.query) return;
					const clientListValues = response.query.map((client) => ({
						id: client.id as string,
						label: client.fullName,
					}));

					if (!clientListValues.length) {
						setSelectedClient(null);

						return;
					}
					setClientList(clientListValues);
				});
		}, 1000);

		// TODO - clearTimeout to void
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [userId, pageSize, searchString, dispatch]);

	const onStartTimeChange = (value: Dayjs | null) => {
		setStartTimeValue(value);

		if (value && (!endTimeValue || (startTimeValue && endTimeValue.isSame(startTimeValue.add(1, 'h'))))) {
			setEndTimeValue(value.add(1, 'h'));
		}
	};

	useEffect(() => {
		const selectedDate = dayjs()
			.set('year', selectedDay.year)
			.set('month', selectedDay.month)
			.set('date', selectedDay.day);
		setDateValue(selectedDate);
	}, [selectedDay]);

	const onClientChange = (value: string) => {
		const client = clientList.find((x) => x.id === value);
		if (!client) return;
		setSelectedClient(client);
	};

	const onTypeChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setSessionTypeId(parseInt(value));
	};

	const clientTimeZoneId = userClients?.query?.find((c) => c.id === clientId)?.timeZoneId;

	const addSession = () => {
		if (!startTimeValue || !endTimeValue || !days.length || !dateValue || !clientId) return;
		setLoading(true);

		const startDate = dateValue.set('hour', startTimeValue.hour()).set('minute', startTimeValue.minute()).toDate();
		const endDate = dateValue.set('hour', endTimeValue.hour()).set('minute', endTimeValue.minute()).toDate();

		const startTime = toClientDateTime(startDate, clientTimeZoneId);
		const endTime = toClientDateTime(endDate, clientTimeZoneId);

		const data: CreateEventProperties = {
			clientId,
			date: startTime.split('T')[0],
			startTime,
			endTime,
			sessionTypeId,
		};

		dispatch(createEvent(data))
			.then(unwrapResult)
			.then(() => {
				toast('Created');
				onClose();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		const data = sessionTypesData.filter((sessionType) =>
			sessionType.allowedRoleSections?.find((x) => x.id === role?.section.id),
		);
		setSessionTypes(data);
		if (!isBT && data.length !== 1) return;
		const btType = data.find((st: SessionTypeProperties) => st.type === SessionTypeEnum.BehaviorTreatment && !st.funderId);
		if (btType) setSessionTypeId(btType.id);
	}, [sessionTypesData, role, isBT]);

	const searchClientHandler = (value: string) => {
		setSearchString(value);
	};

	useEffect(() => {
		const selectedType = sessionTypesData.find((st: SessionTypeProperties) => st.id === sessionTypeId);
		if (!sessionTypeId || selectedType?.type !== SessionTypeEnum.DiscretionarySession || !dateValue) return;
		const date = dateNormalizer(dateValue);
		dispatch(getUserRemainingHours({ userId, date, sessionType: sessionTypeId }));
	}, [dispatch, userId, sessionTypeId, dateValue]);

	return (
		<>
			<SearchSelect
				data={clientList}
				label="Client"
				setSearchString={searchClientHandler}
				searchString={searchString}
				setValue={onClientChange}
				hasMore={Boolean(userClients.hasNextPage)}
				setSize={setPageSize}
				startAdornment={<Client />}
				placeholder="Select client"
			/>
			<CustomSelect
				label="Session type"
				data={sessionTypes}
				value={`${sessionTypeId}`}
				setValue={onTypeChange}
				className="marginBottom16"
				startAdornment={<SessionType />}
				disabled={!clientId}
				description={
					userSessionAllocation
						? `${userSessionAllocation.remainingHoursThisMonth} hours remaining out of ${userSessionAllocation.allocatedHoursPerMonth}.`
						: ''
				}
			/>
			<Picker.CustomDate label="Date of session" onChange={setDateValue} value={dateValue} className="marginBottom16" />
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={startTimeValue} onChange={onStartTimeChange} />
				<Picker.CustomTime
					label="End time"
					value={endTimeValue}
					onChange={setEndTimeValue}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<CustomButton
				onClick={addSession}
				title="Add calendar event"
				loading={loading}
				disabled={!startTimeValue || !endTimeValue || !clientId || hasError || clientId === '0' || sessionTypeId === 0}
			/>
		</>
	);
};

const AddEvent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const isAddingSession = useSelector((state) => state.calendar.isCreating);
	const open = useSelector((state) => state.calendarHelper.popups.addScheduled);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;

	const closeHandler = () => {
		dispatch(setAddScheduledSession(false));
		dispatch(clearDiscretionarySessionState());
	};

	return (
		<CustomAlert
			isBusy={isAddingSession}
			open={open}
			onClose={closeHandler}
			titleOnTop={false}
			title="Add session"
			Content={() => (isAdmin ? <AddContentAdmin /> : <AddContent onClose={closeHandler} />)}
		/>
	);
};

export default AddEvent;
